import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { adminApi, OrganizationAdmin } from '../../api/admin';
import { useDataTableState, usePersistentState } from '../../hooks';

export const OrganizationsTab: React.FC = () => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [organizations, setOrganizations] = useState<OrganizationAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState<OrganizationAdmin | null>(null);
  const [orgName, setOrgName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Persistent filter and sort state
  const { sortField, sortOrder, handleSort, filters } = useDataTableState({
    storageKey: 'admin:organizations',
    defaultSort: { field: 'name', order: 1 },
    defaultFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  });

  // Persistent global search
  const [globalFilterValue, setGlobalFilterValue] = usePersistentState('filters:admin:organizations:globalSearch', '');

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.listOrganizations(1, 100);
      setOrganizations(response.items);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load organizations',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingOrg(null);
    setOrgName('');
    setShowDialog(true);
  };

  const openEditDialog = (org: OrganizationAdmin) => {
    setEditingOrg(org);
    setOrgName(org.name);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!orgName.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Organization name is required',
        life: 3000,
      });
      return;
    }

    try {
      setIsSaving(true);
      if (editingOrg) {
        const updated = await adminApi.updateOrganization(editingOrg.id, {
          name: orgName,
        });
        setOrganizations(
          organizations.map((o) => (o.id === updated.id ? updated : o))
        );
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Organization updated successfully',
          life: 3000,
        });
      } else {
        const created = await adminApi.createOrganization({ name: orgName });
        setOrganizations([created, ...organizations]);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Organization created successfully',
          life: 3000,
        });
      }
      setShowDialog(false);
      setOrgName('');
      setEditingOrg(null);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to save organization',
        life: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (org: OrganizationAdmin) => {
    confirmDialog({
      message: `Are you sure you want to delete "${org.name}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'ms-out ms-out-warning',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await adminApi.deleteOrganization(org.id);
          setOrganizations(organizations.filter((o) => o.id !== org.id));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Organization deleted successfully',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to delete organization',
            life: 5000,
          });
        }
      },
    });
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editingOrg) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Invalid File',
        detail: 'Please upload a PNG, JPEG, or WebP image',
        life: 3000,
      });
      return;
    }

    try {
      setIsUploadingLogo(true);
      const updated = await adminApi.uploadOrganizationLogo(editingOrg.id, file);
      setOrganizations(
        organizations.map((o) => (o.id === updated.id ? updated : o))
      );
      setEditingOrg(updated);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Logo uploaded successfully',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to upload logo',
        life: 5000,
      });
    } finally {
      setIsUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
  };

  const actionBodyTemplate = (rowData: OrganizationAdmin) => {
    return (
      <div className="row-actions">
        <Button
          icon="ms-out ms-out-edit"
          link
          tooltip="Edit"
          onClick={() => openEditDialog(rowData)}
        />
        <Button
          icon="ms-out ms-out-delete"
          link
          tooltip="Delete"
          onClick={() => handleDelete(rowData)}
        />
      </div>
    );
  };

  const dateBodyTemplate = (rowData: OrganizationAdmin) => {
    return new Date(rowData.created_at).toLocaleDateString();
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="controls-panel">
        <Button
          label="Add Organization"
          icon="ms-out ms-out-add"
          onClick={openCreateDialog}
        />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder="Search..."
        />
      </div>

      <DataTable
        value={organizations}
        dataKey="id"
        loading={isLoading}
        paginator
        rows={25}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout:
            'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of{' '}
              <b>{options.totalRecords}</b> organizations
            </span>
          ),
        }}
        stripedRows
        filters={filters}
        globalFilterFields={['name']}
        globalFilter={globalFilterValue}
        emptyMessage="No organizations found"
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={handleSort}
      >
        <Column field="name" header="Name" sortable filter  style={{ width: '20%' }} />
        <Column field="user_count" header="Users" sortable  style={{ width: '10%' }} />
        <Column
          field="created_at"
          header="Created"
          body={dateBodyTemplate}
          sortable
          style={{ width: '10%' }} 
        />
        <Column body={actionBodyTemplate}/>
      </DataTable>

      <Dialog
        header={editingOrg ? 'Edit Organization' : 'Add Organization'}
        visible={showDialog}
        className="organization-dialog"
        onHide={() => {
          setShowDialog(false);
          setOrgName('');
          setEditingOrg(null);
        }}
        footer={
          <>
            <Button
              label="Cancel"
              icon="ms-out ms-out-close"
              onClick={() => setShowDialog(false)}
              outlined
            />
            <Button
              label={editingOrg ? 'Update' : 'Create'}
              icon="ms-out ms-out-check"
              onClick={handleSave}
              severity="success"
              loading={isSaving}
            />
          </>
        }
      >
        <div className="form-field">
          <label htmlFor="orgName">Name *</label>
          <InputText
            id="orgName"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="Enter organization name"
            className="full-width-input"
            autoFocus
          />
        </div>

        {editingOrg && (
          <div className="form-field">
            <label>Logo</label>
            <div className="logo-upload-section">
              {editingOrg.logo_url && (
                <img
                  src={editingOrg.logo_url}
                  alt="Organization logo"
                  className="org-logo-preview"
                />
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />
              <Button
                label={editingOrg.logo_url ? 'Change Logo' : 'Upload Logo'}
                icon="ms-out ms-out-upload"
                onClick={() => fileInputRef.current?.click()}
                outlined
                loading={isUploadingLogo}
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default OrganizationsTab;
